/**
 * The camera + decode mechanism shared by every real check-in scan surface
 * (the door PWA's full-bleed chrome, an admin desktop tab's bounded chrome,
 * and the designer studio's proof) — getUserMedia acquisition, the decode
 * Worker round trip, the torch capability, and the DOMException taxonomy.
 * None of it renders: the `<video>` element and its layout are the host
 * chrome's own (`getVideo` reads it back), and the decode scratch `<canvas>`
 * never mounts, so it lives here as a private implementation detail.
 *
 * Acquisition goes through `CameraAcquisitionStrategy` — a function seam
 * defaulting to the real `navigator.mediaDevices.getUserMedia` call, so a
 * proof surface can reject with a chosen `DOMException` through the exact
 * code path production runs instead of a second hand-rolled mechanism.
 *
 * `events$` is a cold, per-subscription session: acquire → decode loop →
 * (on error) camera-error taxonomy, replayed from the top on `retry()` or on
 * the OS granting camera permission after a prior `notAllowed`.
 *
 * A token is emitted only if it is still the truth about what is in front of the
 * camera. An answer is dropped if the engine was suspended, hidden, retried or
 * stopped since its frame was captured, or if a newer frame has already produced a
 * token, and a ticket held in frame fires once, not once per cooldown.
 */
import {
	BehaviorSubject,
	catchError,
	defer,
	distinctUntilChanged,
	EMPTY,
	filter,
	from,
	fromEvent,
	ignoreElements,
	interval,
	map,
	merge,
	mergeMap,
	type Observable,
	of,
	scan as rxScan,
	Subject,
	share,
	switchMap,
	takeUntil,
	tap,
} from "rxjs";
import {
	createDecodeLane,
	type Frame,
	type LaneAnswer,
} from "./decode/lane.ts";

// A non-standard MediaTrackCapabilities/MediaTrackConstraintSet extension
// (torch) real Android Chrome implements but no TS DOM lib version carries —
// declaration-merged onto the ambient global interfaces rather than cast at
// every call site.
declare global {
	interface MediaTrackCapabilities {
		torch?: boolean;
	}
	interface MediaTrackConstraintSet {
		torch?: boolean;
	}
}

const DECODE_INTERVAL_MS = 200;
// A wedged worker's limit. The costliest 720p frame measured takes about 500ms here
// and 3.2s at 6x slower, so a frame past this is on a phone about 12x slower, and
// its token would name someone who has left. A hung detector never gets here: the
// worker races it.
const DECODE_STALLED_MS = 6000;
// The inverted lane spends at most a quarter of its time decoding, on a core of
// its own: a scanner idling with no code in frame pays for it on every tick.
const INVERTED_PASS_REST_FACTOR = 3;
// However slow its last pass, a light ticket waits no longer than this for the next.
const INVERTED_PASS_REST_CAP_MS = 4000;
// A normal pass this slow means the phone is at full stretch, and a normal
// ticket's read comes first: the inverted attempt is what it gives up.
const INVERTED_PASS_NORMAL_CEILING_MS = 750;
// How long a ticket must have been out of sight to count as scanned again.
const DUPLICATE_SCAN_COOLDOWN_MS = 2000;
// Older sightings cannot matter: no two reads of one lane are this far apart.
const SIGHTING_FORGOTTEN_MS = 30_000;

export type CameraAcquisitionStrategy = (
	constraints: MediaStreamConstraints,
) => Promise<MediaStream>;

const acquireCameraStream: CameraAcquisitionStrategy = (constraints) =>
	navigator.mediaDevices.getUserMedia(constraints);

export type CameraErrorKind =
	| "notAllowed"
	| "notFound"
	| "notReadable"
	| "overconstrained"
	// Not the camera's fault, but the same dead end: nothing here can read a frame.
	| "decoderUnavailable";

// Real, cheap, and grounded in the browser's own error taxonomy — never a
// guessed message-text match. Any DOMException name outside these four
// (AbortError, SecurityError, an unsupported-API TypeError) is rare enough
// that "check camera permissions" stays the most broadly actionable default.
function classifyCameraError(err: unknown): CameraErrorKind {
	const name = err instanceof DOMException ? err.name : "";
	return name === "NotFoundError"
		? "notFound"
		: name === "NotReadableError"
			? "notReadable"
			: name === "OverconstrainedError"
				? "overconstrained"
				: "notAllowed";
}

export type ScanEvent =
	| { readonly kind: "cameraError"; readonly error: CameraErrorKind | null }
	| { readonly kind: "torchAvailable"; readonly available: boolean }
	| { readonly kind: "torchOn"; readonly on: boolean }
	| { readonly kind: "token"; readonly token: string };

export type ScanEngineOptions = {
	// A ref-read, not the element itself — the host chrome owns the `<video>`
	// and may not have rendered it yet at engine-construction time.
	readonly getVideo: () => HTMLVideoElement | undefined;
	readonly constraints: MediaStreamConstraints;
	readonly acquire?: CameraAcquisitionStrategy;
	// Awaited before the first video read on every acquisition attempt — the
	// host chrome's own "the video element exists now" promise (Lit's
	// `updateComplete`, or `Promise.resolve()` when the video is already
	// there by construction).
	readonly ready?: () => Promise<unknown>;
};

export type ScanEngine = {
	readonly events$: Observable<ScanEvent>;
	retry(): void;
	toggleTorch(): Promise<boolean>;
	// Suspends the decode readback without touching the camera. Nests: an
	// overlay opened over an overlay resumes only when the last one closes.
	suspendDecoding(): void;
	resumeDecoding(): void;
	stop(): void;
};

export function createScanEngine(options: ScanEngineOptions): ScanEngine {
	const acquire = options.acquire ?? acquireCameraStream;
	const canvas = document.createElement("canvas");
	const stopped$ = new Subject<void>();
	const normalLane = createDecodeLane(DECODE_STALLED_MS, stopped$);
	const invertedLane = createDecodeLane(DECODE_STALLED_MS, stopped$);
	const retry$ = new Subject<void>();
	const torchEvents$ = new Subject<ScanEvent>();
	const suspensions$ = new BehaviorSubject(0);

	let track: MediaStreamTrack | null = null;
	let torchOn = false;
	let lastCameraError: CameraErrorKind | null = null;
	let invertedPassDueAt = 0;
	let captureOrder = 0;
	// Moves whenever the operator or the OS looks away: a frame captured under an
	// older value says nothing about what is in front of the camera now.
	let lookEpoch = 0;
	// The video time the picture froze at when the page was hidden. A locked phone
	// can hand back that same picture on unlock, before the camera restarts.
	let frozenVideoTime: number | null = null;

	function capture() {
		const video = options.getVideo();
		if (!video || video.readyState !== HTMLMediaElement.HAVE_ENOUGH_DATA) {
			return null;
		}
		if (frozenVideoTime !== null && video.currentTime === frozenVideoTime) {
			return null;
		}
		frozenVideoTime = null;
		const ctx = canvas.getContext("2d");
		if (!ctx) return null;
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		const { width, height, data } = ctx.getImageData(
			0,
			0,
			canvas.width,
			canvas.height,
		);
		const frame: Frame = { width, height, buffer: data.buffer };
		return {
			frame,
			stamp: { order: ++captureOrder, epoch: lookEpoch, at: Date.now() },
		};
	}

	type Stamped = LaneAnswer & {
		readonly lane: "normal" | "inverted";
		readonly stamp: { order: number; epoch: number; at: number };
	};

	// A stall is no answer and no measurement. The age check is wall-clock, because a
	// frozen page fires no event and its timers only catch up after the answer lands.
	const stillTrue = (answer: Stamped) =>
		!answer.stalled &&
		answer.stamp.epoch === lookEpoch &&
		Date.now() - answer.stamp.at <= DECODE_STALLED_MS;

	const retry = () => {
		lookEpoch++;
		normalLane.reset();
		invertedLane.reset();
		retry$.next();
	};

	const cameraSession$: Observable<ScanEvent> = merge(
		of(undefined),
		retry$,
	).pipe(
		switchMap(() =>
			merge(
				of<ScanEvent>({ kind: "cameraError", error: null }),
				defer(() =>
					from(
						(async () => {
							await (options.ready?.() ?? Promise.resolve());
							const video = options.getVideo();
							if (!video)
								throw new Error("scan-engine: video element not mounted");
							const stream = await acquire(options.constraints);
							video.srcObject = stream;
							await video.play();
							track = stream.getVideoTracks()[0] ?? null;
						})(),
					),
				).pipe(
					switchMap(() => {
						// Each tick reads the whole camera frame back on the main
						// thread — 6.4ms at 1920x1080, ~32ms per second at this
						// interval, worse on a phone — and buys nothing while an
						// overlay covers the feed. The `MediaStreamTrack` stays live
						// through a suspend: stopping it would black-frame and
						// re-acquire on resume, more flicker than the tax it saves.
						// A fresh `interval` on resume starts new frames only: a frame
						// already out when the overlay opened still answers, and
						// `stillTrue` is what drops it.
						const normalAnswers$ = suspensions$.pipe(
							map((depth) => depth === 0),
							distinctUntilChanged(),
							switchMap((decoding) =>
								decoding ? interval(DECODE_INTERVAL_MS) : EMPTY,
							),
							filter(
								() => normalLane.free && document.visibilityState === "visible",
							),
							map(capture),
							filter((captured) => captured !== null),
							mergeMap(
								({ frame, stamp }): Observable<Stamped> =>
									normalLane
										.read(frame, "dark-on-light")
										.pipe(
											map((answer) => ({ ...answer, stamp, lane: "normal" })),
										),
							),
							filter(stillTrue),
							share(),
						);
						// Runs beside the normal lane rather than after it in one
						// worker, so a slow inverted pass cannot delay the next frame
						// and with it a normal ticket's read. Only a miss reaches it.
						const invertedAnswers$ = normalAnswers$.pipe(
							filter(
								(answer) =>
									answer.token === null &&
									answer.tookMs <= INVERTED_PASS_NORMAL_CEILING_MS &&
									invertedLane.free &&
									performance.now() >= invertedPassDueAt,
							),
							mergeMap(
								({ frame, stamp }): Observable<Stamped> =>
									frame === null
										? EMPTY
										: invertedLane.read(frame, "light-on-dark").pipe(
												map((answer) => ({
													...answer,
													stamp,
													lane: "inverted",
												})),
											),
							),
							filter(stillTrue),
							tap(({ tookMs }) => {
								invertedPassDueAt =
									performance.now() +
									Math.min(
										tookMs * INVERTED_PASS_REST_FACTOR,
										INVERTED_PASS_REST_CAP_MS,
									);
							}),
						);
						return merge(
							of<ScanEvent>({
								kind: "torchAvailable",
								available: track?.getCapabilities?.().torch === true,
							}),
							// After `torchAvailable`, which clears the view's error state.
							normalLane.failed$.pipe(
								filter((failed) => failed),
								map(
									(): ScanEvent => ({
										kind: "cameraError",
										error: "decoderUnavailable",
									}),
								),
							),
							merge(normalAnswers$, invertedAnswers$).pipe(
								// A ticket held still fires once. It counts as gone only when
								// it has been out of sight for the cooldown and its own lane
								// has read a frame without it, because on a slow phone or a
								// resting lane two reads of a held ticket are further apart
								// than the cooldown.
								rxScan(
									(watch, answer) => {
										const read = watch.reads[answer.lane] + 1;
										watch.reads[answer.lane] = read;
										watch.fired = null;
										const { token, stamp, lane } = answer;
										if (token === null || stamp.order < watch.newestOrder) {
											return watch;
										}
										watch.newestOrder = stamp.order;
										const last = watch.seen.get(token);
										if (
											last === undefined ||
											(stamp.at - last.at >= DUPLICATE_SCAN_COOLDOWN_MS &&
												!(last.lane === lane && last.read === read - 1))
										) {
											watch.fired = token;
										}
										watch.seen.set(token, { at: stamp.at, lane, read });
										for (const [seenToken, sighting] of watch.seen) {
											if (stamp.at - sighting.at > SIGHTING_FORGOTTEN_MS) {
												watch.seen.delete(seenToken);
											}
										}
										return watch;
									},
									{
										newestOrder: 0,
										reads: { normal: 0, inverted: 0 },
										seen: new Map<
											string,
											{
												at: number;
												lane: Stamped["lane"];
												read: number;
											}
										>(),
										fired: null as string | null,
									},
								),
								map((watch) => watch.fired),
								filter((token) => token !== null),
								map((token): ScanEvent => ({ kind: "token", token })),
							),
						);
					}),
					catchError((err: unknown) => {
						console.error("[scan-engine] camera acquisition failed", err);
						return of<ScanEvent>({
							kind: "cameraError",
							error: classifyCameraError(err),
						});
					}),
				),
			),
		),
		tap((event) => {
			if (event.kind === "cameraError") lastCameraError = event.error;
		}),
	);

	// Funkhaus-sports' one genuinely good camera-recovery instinct, carried
	// forward for real: the instant the OS grants camera permission,
	// re-attempt acquisition so the operator never has to find their own way
	// back to "Try again". `ignoreElements()` keeps its side effect (the
	// retry) live inside the merged session without ever emitting a
	// `ScanEvent` of its own; isolated with its own `catchError` — Permissions
	// API support gaps or a rejected query must never take down camera decode.
	const cameraPermissionAutoRetry$: Observable<never> =
		"permissions" in navigator
			? from(
					// PermissionName's TS DOM typing doesn't carry "camera" despite
					// every Chromium/Firefox release implementing it — the same
					// class of DOM-lib gap decode/worker.ts's BarcodeDetector cast
					// already documents.
					navigator.permissions.query({ name: "camera" as PermissionName }),
				).pipe(
					switchMap((status) =>
						fromEvent(status, "change").pipe(
							tap(() => {
								if (
									status.state === "granted" &&
									lastCameraError === "notAllowed"
								) {
									retry();
								}
							}),
						),
					),
					catchError(() => EMPTY),
					ignoreElements(),
				)
			: EMPTY;

	async function toggleTorch(): Promise<boolean> {
		if (!track) return false;
		const next = !torchOn;
		try {
			await track.applyConstraints({ advanced: [{ torch: next }] });
			torchOn = next;
			torchEvents$.next({ kind: "torchOn", on: next });
			return true;
		} catch (err) {
			console.error("[scan-engine] torch toggle failed", err);
			return false;
		}
	}

	return {
		events$: merge(
			cameraSession$,
			cameraPermissionAutoRetry$,
			torchEvents$,
			fromEvent(document, "visibilitychange").pipe(
				filter(() => document.visibilityState === "hidden"),
				tap(() => {
					lookEpoch++;
					frozenVideoTime = options.getVideo()?.currentTime ?? null;
				}),
				ignoreElements(),
			),
		).pipe(takeUntil(stopped$)),
		retry,
		toggleTorch,
		suspendDecoding: () => {
			lookEpoch++;
			suspensions$.next(suspensions$.value + 1);
		},
		resumeDecoding: () =>
			suspensions$.next(Math.max(0, suspensions$.value - 1)),
		stop: () => {
			track?.stop();
			stopped$.next();
			normalLane.terminate();
			invertedLane.terminate();
		},
	};
}
